import React, { useEffect } from "react";
import Button from "../../presentational/Button";
import { Parallax, ParallaxProvider } from "react-scroll-parallax";

import RotatingHeader from "./RotatingHeader";
import LoginHeader from "./LoginHeader";

import useAuthenticationStore, { AuthenticationViews } from "./Store";
import LocalizedText from '../../presentational/LocalizedText';
import useTranslate from "../../../hooks/useTranslate";
import "./ParticipantLanding.css";
import LoginFooter from "./LoginFooter";
import { useAuthenticationConfiguration } from "./Configuration";
import { ProductNames } from "./Authentication";
import { getIdentifier, getOperatingSystem } from "../utils";
import CookiesBanner from "./CookiesBanner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeartPulse } from "@fortawesome/pro-regular-svg-icons";
import { faLungs } from "@fortawesome/pro-regular-svg-icons";
import { faVirus } from "@fortawesome/pro-regular-svg-icons";
import { faHeadSideBrain } from "@fortawesome/pro-regular-svg-icons";
import { faWatchApple } from "@fortawesome/pro-regular-svg-icons";
import { faFilesMedical } from "@fortawesome/pro-regular-svg-icons";
import { faChartMixed } from "@fortawesome/pro-regular-svg-icons";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";

export default function () {
  const configuration = useAuthenticationConfiguration()
  const { state, dispatch, setActiveView, loadProject, checkLogin, validateIdentifier, getLoginCookie } = useAuthenticationStore()
  const { currentLanguage, redirectUrl } = state
  const { productName } = configuration;
  const translations = useTranslate(["download-the-app"])

  // State for cycling trusted logos
  const [currentLogoSetIndex, setCurrentLogoSetIndex] = React.useState(0);
  const [logoSetIsAnimating, setLogoSetIsAnimating] = React.useState(false);
  const logoSets = [
    [
      { text: "CDC", type: "text" },
      { text: "NIH", type: "text" },
      { alt: "university of massachusetts medical school", opacity: 0.8, src: "https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-umass.png", type: "image" },
      { alt: "Scripps", src: "https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-scripps.png", opacity: 0.5, type: "image" },
      { text: "Dexcom", type: "text" },
    ],
    [
      { text: "University of Michigan", type: "text" },
      { alt: "ALS Association", src: "https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-als.png", opacity: 0.7, type: "image" },
      { text: "Apple", type: "text" },
      { alt: "massachusetts general hospital", opacity: 0.9, src: "https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-massgen.png", type: "image" },
      { text: "SolveME/Unhide", type: "text" },
    ],
    [
      { text: "Tempus", type: "text" },
      { alt: "Framingham Heart Study", height: "90px", opacity: 0.75, src: "https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-fhs.png", type: "image" },
      { text: "Mayo Clinic", type: "text" },
      { alt: "All of Us", opacity: 0.7, height: "50px", src: "https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-aou.png", type: "image" },
    ]
  ];

  useEffect(() => {
    async function preparePage() {
      const knownUserEmailOrPhone = getLoginCookie()
      if (knownUserEmailOrPhone) {
        setActiveView(AuthenticationViews.knownUserLogin, { replace: true });
      }
      else if (productName !== ProductNames.MyDataHelps) {
        setActiveView(AuthenticationViews.splash)
      }

      let newRedirectUrl = redirectUrl
      const identifier = getIdentifier()
      if (identifier) {
        newRedirectUrl = await validateIdentifier(identifier, newRedirectUrl)
      }

      newRedirectUrl = await loadProject(newRedirectUrl);
      if (newRedirectUrl) {
        dispatch({ type: 'SET_LOGIN_CHECK_TIMER', payload: window.setInterval(checkLogin, 1500) });
      }
    }
    preparePage()
  }, []);

  // Auto-cycle through logo sets
  useEffect(() => {
    const interval = setInterval(() => {
      setLogoSetIsAnimating(true);
      setTimeout(() => {
        setCurrentLogoSetIndex((prev) => (prev + 1) % logoSets.length);
        setLogoSetIsAnimating(false);
      }, 300); // Wait for fade-out to complete before starting fade-in
    }, 4000);

    return () => clearInterval(interval);
  }, [logoSets.length]);

  var mobileOS = getOperatingSystem();
  const showDownloadSection = !(mobileOS == "iOS" || mobileOS == "Android");

  return (
    <ParallaxProvider>
      { productName !== ProductNames.MyDataHelps &&
        <LoginHeader productName={productName} />
      }
      <main
        ce-style-variant="marketing"
        id="splash-page"
      >
        <section className="hero">
          <div className="landscape-background">
            <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/mdh-hero-landscape-wide.svg" ></img>
          </div>
          <div className="text-background"></div>
          <div className="container col-2">
            <div className="text-wrap">
              <RotatingHeader />
              <h2 className=""><LocalizedText textKey="ppt-landingpage-hero-heading-1a"/><br /> <LocalizedText textKey="ppt-landingpage-hero-heading-1b" /></h2>
              <div className="text-wrap-small">
              <p><LocalizedText textKey="ppt-landingpage-hero-p" /></p>
                <div className="button-group">
                  <Button
                    className="join-button"
                    size="large"
                    intent='primary'
                    onClick={() => setActiveView(AuthenticationViews.join)}
                  >
                    <LocalizedText textKey="join-now" />
                  </Button>
                  <Button
                    className="login-button"
                    size="large"
                    intent="none"
                    onClick={() => setActiveView(AuthenticationViews.login)}
                  >
                    <LocalizedText textKey="login" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="trusted-by">
          <h3 className="header gradient-text"><LocalizedText textKey="ppt-landingpage-trusted-by-1" /></h3>
          <ul className={`trusted-logos container ${logoSetIsAnimating ? 'fade-out' : 'fade-in'}`}>
            {logoSets[currentLogoSetIndex].map((logo, index) => (
              <li key={index} className="logo-container">
                {logo.type === 'image' ? (
                  <img 
                    src={(logo as any).src} 
                    alt={(logo as any).alt}
                    style={{ 
                      opacity: (logo as any).opacity || 1, 
                      maxHeight: (logo as any).height || 'auto' 
                    }}
                  />
                ) : (
                  <span 
                    className="logo-text"
                    style={{ opacity: (logo as any).opacity || 1 }}
                  >
                    {(logo as any).text}
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p><LocalizedText textKey="ppt-landingpage-trusted-by-2" /></p>
        </section>
        <section className="section-1">
          <div className="container col-2 align-stretch">
            <div className="text-wrap">
              <header>
                <h2><LocalizedText textKey="ppt-landingpage-section-1-heading" /></h2>
              </header>
              <p><LocalizedText textKey="ppt-landingpage-section-1-p-1" /></p>
              <ul className="icon-bullets">
              <li>
                <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                <LocalizedText textKey="ppt-landingpage-section-1-li-1" />
              </li>
              <li>
              <ul className="icon-bullets">
                <li>
                  <span className="icon-wrap">
                    <FontAwesomeIcon icon={faHeartPulse} />
                  </span>
                  <span>
                    <LocalizedText textKey="ppt-landingpage-section-1-li-2" />
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <FontAwesomeIcon icon={faLungs} />
                  </span>
                  <span>
                    <LocalizedText textKey="ppt-landingpage-section-1-li-3" />
                    
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <FontAwesomeIcon icon={faVirus} />
                  </span>
                  <span>
                    <LocalizedText textKey="ppt-landingpage-section-1-li-4" />
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <FontAwesomeIcon icon={faHeadSideBrain} />
                  </span>
                  <span>
                    <LocalizedText textKey="ppt-landingpage-section-1-li-5" />
                  </span>
                </li>
              </ul>   
              </li>
              <li>
                <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                <LocalizedText textKey="ppt-landingpage-section-1-li-6" />
              </li>                
              </ul>
              <small><LocalizedText textKey="ppt-landingpage-section-1-small" /></small>
              <div className="button-group">
                  <Button
                    className="join-button"
                    size="large"
                    intent='primary'
                    onClick={() => setActiveView(AuthenticationViews.join)}
                  >
                    <LocalizedText textKey="join-now" />
                  </Button>
              </div>
            </div>
            <div className="image-wrap" aria-hidden>
              <Parallax rotate={[20, 120]} className="section-1-dial">
                <div className="graphics">
                  <svg width="100" height="100" viewBox="-1 -1 102 102" fill="#000" stroke="#000" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M100 50C100 77.6142 77.6142 100 50 100C22.3858 100 0 77.6142 0 50C0 22.3858 22.3858 0 50 0C77.6142 0 100 22.3858 100 50ZM85.4485 49H96.3576V51H85.4485V49ZM3.64241 51H14.5515V49H3.64241V51ZM75.7729 74.3588L83.4868 82.0727L82.0726 83.4869L74.3587 75.773L75.7729 74.3588ZM16.5131 17.9273L24.227 25.6412L25.6412 24.227L17.9273 16.5131L16.5131 17.9273ZM74.3588 24.227L82.0727 16.5131L83.4869 17.9273L75.773 25.6412L74.3588 24.227ZM17.9273 83.4869L25.6412 75.773L24.227 74.3588L16.5131 82.0727L17.9273 83.4869ZM49 14.5455V3.63636L51 3.63636V14.5455H49ZM51 96.3575V85.4484H49V96.3575H51ZM82.3674 35.5106L92.4461 31.3359L93.2115 33.1836L83.1328 37.3583L82.3674 35.5106ZM7.55387 68.6641L17.6326 64.4894L16.8672 62.6417L6.7885 66.8164L7.55387 68.6641ZM64.4894 82.3674L68.6641 92.4461L66.8164 93.2115L62.6417 83.1328L64.4894 82.3674ZM31.3359 7.55381L35.5106 17.6325L37.3583 16.8671L33.1836 6.78845L31.3359 7.55381ZM83.1328 62.6417L93.2115 66.8164L92.4461 68.6641L82.3674 64.4894L83.1328 62.6417ZM6.7885 33.1835L16.8672 37.3582L17.6326 35.5105L7.55387 31.3358L6.7885 33.1835ZM62.6417 16.8671L66.8164 6.78845L68.6641 7.55381L64.4894 17.6325L62.6417 16.8671ZM33.1836 93.2115L37.3583 83.1328L35.5106 82.3674L31.3359 92.4461L33.1836 93.2115Z"/>
                  </svg>
                </div>
              </Parallax>
              <Parallax speed={8} className="moving-image section-1-image-1">
                <img src="https://assets.careevolutionapps.com/CareEvolution/Images/ParticipantLanding/survey.png" aria-hidden/>
              </Parallax>
              <Parallax speed={-8} className="moving-image section-1-image-2">
                <img src="https://assets.careevolutionapps.com/CareEvolution/Images/ParticipantLanding/SS3.png" aria-hidden/>
              </Parallax>
            </div>
          </div>
        </section>
        <section className="section-2">
          <div className="container">
            <ul className="how-it-works-graphic">
              <li>
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/create.svg" alt="Illustration of woman signing up on her phone" />
                <h3><LocalizedText textKey="ppt-landingpage-section-2-li-1" /></h3>
              </li>
              <li>
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/connect.svg" alt="Illustration of connecting with esearch studies and your healthcare providersr" />
                <h3><LocalizedText textKey="ppt-landingpage-section-2-li-2" /></h3>
              </li>
              <li>
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/contribute.svg" alt="Illustration of device information sharing" />
                <h3><LocalizedText textKey="ppt-landingpage-section-2-li-3" /></h3>
              </li>
            </ul>
            <h3><LocalizedText textKey="ppt-landingpage-section-2-heading" /></h3>
          </div>
        </section>
        <section className="section-3">
          <div className="container col-2 align-stretch">
            <div className="text-wrap">
              <header>
                <h2>
                  <LocalizedText textKey="ppt-landingpage-section-3-heading" />
                  
                </h2>
              </header>
              <p>
                 <LocalizedText textKey="ppt-landingpage-section-3-p-1" />
                
              </p>
              <div className="section-3-section">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom-shark-logo.svg" alt="Symptom Shark Logo" />
                <p><LocalizedText textKey="ppt-landingpage-section-3a-p-1" /></p>
                <div className="list-button-group">
                  <ul className="icon-bullets">
                    <li>
                      <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                      <LocalizedText textKey="ppt-landingpage-section-3a-li-1" />
                    </li>
                    <li>
                      <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                      <LocalizedText textKey="ppt-landingpage-section-3a-li-2" />
                    </li>
                  </ul>
                  <Button
                    className=""
                    intent="none"
                    size="large"
                    href={
                      "https://symptomshark.org/"
                    }
                    target="_blank"
                  >
                    {/* Join Project */}
                    <LocalizedText textKey="join-now" />
                  </Button>
                </div>
              </div>
              <div className="section-3-section">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/asthma-tool-logo.svg" alt="Asthma Tool Logo" />
                <p>
                  <LocalizedText textKey="ppt-landingpage-section-3b-p-1" />
                </p>
                <div className="list-button-group">
                  <ul className="icon-bullets">
                    <li>
                      <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                      
                      <LocalizedText textKey="ppt-landingpage-section-3b-li-1" />
                    </li>
                    <li>
                      <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                      
                      <LocalizedText textKey="ppt-landingpage-section-3b-li-2" />
                    </li>
                    <li>
                      <span className="icon-wrap"><FontAwesomeIcon icon={faCheck} /></span>
                      
                      <LocalizedText textKey="ppt-landingpage-section-3b-li-3" />
                    </li>
                  </ul>
                  <Button
                    className=""
                    intent="none"
                    size="large"
                    href={
                      "https://asthmatool.com/"
                    }
                    target="_blank"
                  >
                    <LocalizedText textKey="join-now" />
                  </Button>
                </div>
              </div>
            </div>
            <div className="image-wrap" aria-hidden>
              <Parallax rotate={[120, 20]} className="section-3-dial">
                <div className="graphics">
                  <svg width="68" height="67" viewBox="0 0 68 67" fill="#000" stroke="#000" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="34" cy="33.5" r="27.5" strokeWidth="0"/>
                    <path d="M67.5 33.5H64.5"/>
                    <path d="M0.5 33.5H3.5"/>
                    <path d="M57.6881 57.188L55.5667 55.0667"/>
                    <path d="M10.3119 9.81195L12.4332 11.9333"/>
                    <path d="M57.6881 9.81195L55.5667 11.9333"/>
                    <path d="M10.3119 57.188L12.4332 55.0667"/>
                    <path d="M34 0L34 3"/>
                    <path d="M34 67L34 64"/>
                    <path d="M64.95 20.6801L62.1783 21.8282"/>
                    <path d="M3.05003 46.3199L5.82167 45.1718"/>
                    <path d="M46.8199 64.45L45.6718 61.6783"/>
                    <path d="M21.1801 2.55005L22.3281 5.32169"/>
                    <path d="M64.95 46.3199L62.1783 45.1718"/>
                    <path d="M3.05003 20.6801L5.82167 21.8281"/>
                    <path d="M46.8199 2.55005L45.6718 5.32169"/>
                    <path d="M21.1801 64.45L22.3281 61.6783"/>
                    <path d="M66.8563 26.9645L63.9139 27.5497"/>
                    <path d="M1.14371 40.0355L4.08606 39.4503"/>
                    <path d="M61.8542 52.1116L59.3598 50.4449"/>
                    <path d="M6.14575 14.8884L8.64016 16.5551"/>
                    <path d="M52.6116 5.64581L50.9449 8.14022"/>
                    <path d="M15.3884 61.3542L17.0551 58.8598"/>
                    <path d="M27.4645 0.643677L28.0497 3.58603"/>
                    <path d="M40.5355 66.3563L39.9503 63.4139"/>
                    <path d="M61.8542 14.8884L59.3598 16.5551"/>
                    <path d="M6.14577 52.1116L8.64018 50.4449"/>
                    <path d="M52.6116 61.3542L50.9449 58.8598"/>
                    <path d="M15.3884 5.64569L17.0551 8.1401"/>
                    <path d="M66.8563 40.0355L63.9139 39.4503"/>
                    <path d="M1.14368 26.9644L4.08603 27.5497"/>
                    <path d="M40.5355 0.643677L39.9502 3.58603"/>
                    <path d="M27.4645 66.3563L28.0497 63.4139"/>
                  </svg>
                </div>
              </Parallax>
              <Parallax speed={2} className="moving-image section-3-2">
                <img src="https://assets.careevolutionapps.com/CareEvolution/Images/ParticipantLanding/AT.png" alt="Screenshot of Asthma Tool" />
              </Parallax>
              <Parallax speed={-5} className="moving-image section-3-1">
                <img src="https://assets.careevolutionapps.com/CareEvolution/Images/ParticipantLanding/ssdash.png" alt="Screenshot of Symptom Shark" />
              </Parallax>
            </div>
          </div>
        </section>
        <section className="section-4">
          <div className="container col-2 align-stretch">
            <div className="image-wrap" aria-hidden>
              <Parallax rotate={[20, 120]} className="section-4-dial">
                <div className="graphics">
                  <svg width="100" height="100" viewBox="-1 -1 102 102" fill="#fff" stroke="#fff" xmlns="http://www.w3.org/2000/svg">
                    <path fillRule="evenodd" clipRule="evenodd" d="M100 50C100 77.6142 77.6142 100 50 100C22.3858 100 0 77.6142 0 50C0 22.3858 22.3858 0 50 0C77.6142 0 100 22.3858 100 50ZM85.4485 49H96.3576V51H85.4485V49ZM3.64241 51H14.5515V49H3.64241V51ZM75.7729 74.3588L83.4868 82.0727L82.0726 83.4869L74.3587 75.773L75.7729 74.3588ZM16.5131 17.9273L24.227 25.6412L25.6412 24.227L17.9273 16.5131L16.5131 17.9273ZM74.3588 24.227L82.0727 16.5131L83.4869 17.9273L75.773 25.6412L74.3588 24.227ZM17.9273 83.4869L25.6412 75.773L24.227 74.3588L16.5131 82.0727L17.9273 83.4869ZM49 14.5455V3.63636L51 3.63636V14.5455H49ZM51 96.3575V85.4484H49V96.3575H51ZM82.3674 35.5106L92.4461 31.3359L93.2115 33.1836L83.1328 37.3583L82.3674 35.5106ZM7.55387 68.6641L17.6326 64.4894L16.8672 62.6417L6.7885 66.8164L7.55387 68.6641ZM64.4894 82.3674L68.6641 92.4461L66.8164 93.2115L62.6417 83.1328L64.4894 82.3674ZM31.3359 7.55381L35.5106 17.6325L37.3583 16.8671L33.1836 6.78845L31.3359 7.55381ZM83.1328 62.6417L93.2115 66.8164L92.4461 68.6641L82.3674 64.4894L83.1328 62.6417ZM6.7885 33.1835L16.8672 37.3582L17.6326 35.5105L7.55387 31.3358L6.7885 33.1835ZM62.6417 16.8671L66.8164 6.78845L68.6641 7.55381L64.4894 17.6325L62.6417 16.8671ZM33.1836 93.2115L37.3583 83.1328L35.5106 82.3674L31.3359 92.4461L33.1836 93.2115Z"/>
                  </svg>
                </div>
              </Parallax>
              <Parallax speed={8} className="moving-image section-4-image-1">
                <img src="https://assets.careevolutionapps.com/CareEvolution/Images/ParticipantLanding/results-1.png" aria-hidden/>
              </Parallax>
              <Parallax speed={-8} className="moving-image section-4-image-2">
                <img src="https://assets.careevolutionapps.com/CareEvolution/Images/ParticipantLanding/dashboard-1.png" aria-hidden/>
              </Parallax>
            </div>
            <div className="text-wrap">
              <header>
                <h2>
                  
                  <LocalizedText textKey="ppt-landingpage-section-4-heading" />
                </h2>
              </header>
              <p>
                <LocalizedText textKey="ppt-landingpage-section-4-p-1" />
              </p>
              <ul className="icon-bullets">
                <li>
                    <span className="icon-wrap"><FontAwesomeIcon icon={faWatchApple} /></span>
                    <LocalizedText textKey="ppt-landingpage-section-4-li-1" />
                    
                </li>
                <li>
                    <span className="icon-wrap"><FontAwesomeIcon icon={faFilesMedical} /></span>
                    
                    <LocalizedText textKey="ppt-landingpage-section-4-li-2" />
                </li>
                <li>
                    <span className="icon-wrap"><FontAwesomeIcon icon={faChartMixed} /></span>
                    
                    <LocalizedText textKey="ppt-landingpage-section-4-li-3" />
                </li>
              </ul>
              <div className="button-group">
                <Button
                    className="join-button"
                    size="large"
                    intent='primary'
                    onClick={() => setActiveView(AuthenticationViews.join)}
                  >
                    <LocalizedText textKey="join-now" />
                </Button>
              </div>
              { showDownloadSection && (
                <>
                  <h3 className="header">
                    <LocalizedText textKey="ppt-landingpage-download-the-app" />
                  </h3>
                  <div className="app-stores container">
                    <a target="_blank" href="https://apps.apple.com/us/app/mydatahelps/id1286789190">
                      <img src={`https://appstorebadges.careevolutionapps.com/images/apple/black/logo-${currentLanguage}.svg`} alt={`${translations["download-the-app"]} iOS`} />
                    </a>
                    <a target="_blank" href="https://play.google.com/store/apps/details?id=com.careevolution.mydatahelps&hl=en_US">
                      <img src={`https://appstorebadges.careevolutionapps.com/images/google/black/logo-${currentLanguage}.svg`} alt={`${translations["download-the-app"]} Android`} />
                    </a>
                  </div>
                </>
              )
            }
            </div>
          </div>
        </section>
        <section className="help-section">
          <div className="container">
            <div>
              <h2>
                <LocalizedText textKey="ppt-landingpage-section-help-heading" />
              </h2>
              <p>
                <LocalizedText textKey="ppt-landingpage-section-help-p" />
              </p>
            </div>
            <Button
              className="help-center-button"
              size="large"
              href="https://help.mydatahelps.org/"
              target="_blank"
            >
             
              <LocalizedText textKey="ppt-landingpage-section-help-button" />
            </Button>
          </div>
        </section>
        <section className="join-section">
          <div className="container">
            <svg width="238" height="34" viewBox="0 0 238 34" fill="#000" stroke="#000" xmlns="http://www.w3.org/2000/svg">
              <path d="M53.137 27.5579H56.1734V11.8105L61.8889 27.5579H65.7291L71.534 11.8105V27.5579H74.5704V8.32104H69.8371L63.8537 25.1421L57.7809 8.32104H53.137V27.5579Z" strokeWidth="0"/>
              <path d="M88.5021 14.0474L84.394 24.0684L80.018 14.0474H76.8923L82.8758 27.2L82.4293 28.2737C81.6255 30.2421 81.179 30.6895 79.3929 30.6895H78.0533V33.1053H79.6608C82.8758 33.1053 83.5902 31.9421 84.9298 29.0789L91.6278 13.9579H88.5021V14.0474Z" strokeWidth="0"/>
              <path d="M93.6818 8.32104V27.5579H101.63C107.524 27.5579 111.364 23.8895 111.364 17.8053C111.364 11.5421 107.435 8.32104 101.362 8.32104H93.6818ZM100.916 25.2316H96.8075V10.7368H101.094C106.006 10.7368 108.328 13.1526 108.328 17.9842C108.239 22.6368 105.917 25.2316 100.916 25.2316Z" strokeWidth="0"/>
              <path d="M118.152 27.8263C120.831 27.8263 122.26 26.5737 122.974 25.5895C122.974 26.2158 123.063 27.021 123.063 27.5579H126.1C125.921 26.4842 125.832 25.1421 125.832 24.1579V19.0579C125.832 14.7631 122.528 13.6895 119.67 13.6895C116.455 13.6895 113.865 14.9421 113.418 18.0737H116.455C116.723 16.5526 118.062 15.9263 119.759 15.9263C121.724 15.9263 122.974 16.821 122.974 18.521V19.0579L120.206 19.2368C116.455 19.4158 112.883 20.221 112.883 23.7105C112.883 26.2158 115.026 27.8263 118.152 27.8263ZM118.687 25.6789C116.901 25.6789 115.83 24.7842 115.83 23.5316C115.83 21.6526 118.42 21.2052 120.831 21.1158L122.974 21.0263V21.8316C122.885 24.7842 120.474 25.6789 118.687 25.6789Z" strokeWidth="0"/>
              <path d="M133.155 27.5578H136.549V25.2315H134.227C133.066 25.2315 132.619 24.6947 132.619 23.5315V16.1947H136.459V13.9578H132.619V10.1105H129.672V13.9578H127.171V16.1947H129.672V24.0684C129.672 26.3052 131.101 27.5578 133.155 27.5578Z" strokeWidth="0"/>
              <path d="M142.979 27.8263C145.658 27.8263 147.087 26.5737 147.801 25.5895C147.801 26.2158 147.89 27.021 147.89 27.5579H150.927C150.748 26.4842 150.659 25.1421 150.659 24.1579V19.0579C150.659 14.7631 147.355 13.6895 144.497 13.6895C141.282 13.6895 138.692 14.9421 138.245 18.0737H141.282C141.55 16.5526 142.889 15.9263 144.586 15.9263C146.551 15.9263 147.801 16.821 147.801 18.521V19.0579L145.033 19.2368C141.282 19.4158 137.71 20.221 137.71 23.7105C137.71 26.2158 139.853 27.8263 142.979 27.8263ZM143.514 25.6789C141.728 25.6789 140.657 24.7842 140.657 23.5316C140.657 21.6526 143.247 21.2052 145.658 21.1158L147.801 21.0263V21.8316C147.712 24.7842 145.301 25.6789 143.514 25.6789Z" strokeWidth="0"/>
              <path d="M153.874 8.32104V27.5579H156.91V18.8789H167.895V27.5579H170.931V8.32104H167.895V16.5526H156.91V8.32104H153.874Z" strokeWidth="0"/>
              <path d="M187.721 21.6526V20.8473C187.721 16.4631 185.042 13.6895 180.844 13.6895C176.558 13.6895 173.789 16.5526 173.789 20.7579C173.789 24.9631 176.647 27.8263 180.934 27.8263C183.97 27.8263 186.828 26.4842 187.453 23.2631H184.506C183.97 25.1421 182.184 25.5895 180.844 25.5895C178.433 25.5895 177.093 23.9789 176.915 21.7421H187.721V21.6526ZM180.844 16.0158C183.077 16.0158 184.506 17.4473 184.684 19.6842H176.826C177.093 17.4473 178.433 16.0158 180.844 16.0158Z" strokeWidth="0"/>
              <path d="M190.4 8.32104V27.5579H193.347V8.32104H190.4Z" strokeWidth="0"/>
              <path d="M204.6 13.6895C202.367 13.6895 200.759 14.6737 199.956 15.9263V13.9579H197.009V33.1947H199.956V25.5895C200.849 26.9316 202.367 27.8263 204.6 27.8263C208.261 27.8263 210.851 25.0526 210.851 20.7579C210.94 16.4631 208.261 13.6895 204.6 13.6895ZM203.796 16.1052C206.296 16.1052 207.815 18.0737 207.815 20.8473C207.815 23.621 206.296 25.5895 203.796 25.5895C201.206 25.5895 199.777 23.5316 199.777 20.8473C199.777 18.1631 201.295 16.1052 203.796 16.1052Z" strokeWidth="0"/>
              <path d="M219.156 27.9158C222.729 27.9158 225.408 26.3947 225.408 23.4421C225.408 20.6684 223.086 20.0421 218.978 19.2368C217.192 18.8789 215.941 18.7 215.941 17.5368C215.941 16.4631 217.192 15.8368 218.889 15.8368C220.496 15.8368 221.836 16.4631 221.925 18.1631H224.961C224.693 14.7631 221.657 13.6895 218.889 13.6895C215.495 13.6895 212.905 15.0316 212.905 17.7158C212.905 20.6684 216.209 21.0263 218.889 21.5631C220.764 21.921 222.371 22.2789 222.371 23.7105C222.371 24.8737 221.21 25.8579 219.156 25.8579C217.281 25.8579 215.674 25.0526 215.495 23.3526H212.459C212.726 26.8421 216.209 27.9158 219.156 27.9158Z" strokeWidth="0"/>
              <path d="M34.3827 6.62102L25.2735 15.7473C24.827 15.0315 24.2019 14.4052 23.4874 13.9579L32.5966 4.83154L34.3827 6.62102Z" strokeWidth="0"/>
              <path d="M14.3782 15.7473L5.35835 6.62102L7.14447 4.83154L16.1644 13.8684C15.5392 14.4052 14.9141 15.0315 14.3782 15.7473Z" strokeWidth="0"/>
              <path d="M38.1336 13.1526L26.3452 18.0737C26.1666 17.1789 25.8094 16.3737 25.3629 15.7473L37.1512 10.8263L38.1336 13.1526Z" strokeWidth="0"/>
              <path d="M39.1159 20.6685H26.2559C26.3452 20.2211 26.3452 19.8632 26.3452 19.4158C26.3452 18.9685 26.3452 18.5211 26.2559 18.1632H39.0266V20.6685H39.1159Z" strokeWidth="0"/>
              <path d="M14.3782 15.7473C13.9317 16.4631 13.5745 17.2684 13.3959 18.0737L1.6075 13.1526L2.58986 10.8263L14.3782 15.7473Z" strokeWidth="0"/>
              <path d="M13.3066 19.4158C13.3066 19.8632 13.3066 20.3106 13.3959 20.6685H0.625137V18.0737H13.3959C13.3066 18.5211 13.3066 18.9685 13.3066 19.4158Z" strokeWidth="0"/>
              <path d="M16.2537 24.8737L7.14447 33.9105L5.35835 32.1211L14.3782 23.0842C14.9141 23.7105 15.5392 24.3369 16.2537 24.8737Z" strokeWidth="0"/>
              <path d="M32.5966 33.9105L23.4874 24.8737C24.2019 24.4263 24.827 23.8 25.2735 23.0842L34.2934 32.1211L32.5966 33.9105Z" strokeWidth="0"/>
              <path d="M37.1512 27.9158L25.3629 22.9948C25.8094 22.279 26.1666 21.4737 26.3452 20.6685L38.1336 25.5895L37.1512 27.9158Z" strokeWidth="0"/>
              <path d="M13.3959 20.6685C13.5745 21.5632 13.9317 22.3685 14.3782 22.9948L2.58986 27.9158L1.6075 25.5895L13.3959 20.6685Z" strokeWidth="0"/>
              <path d="M13.6638 1.07373L18.5756 12.8843C17.6826 13.0632 16.8788 13.4211 16.2537 13.8685L11.3418 2.05794L13.6638 1.07373Z" strokeWidth="0"/>
              <path d="M28.3993 2.05794L23.4874 13.8685C22.773 13.4211 21.9692 13.0632 21.1655 12.8843L26.0773 1.07373L28.3993 2.05794Z" strokeWidth="0"/>
              <path d="M21.1655 0.0894775V12.8842C20.719 12.7947 20.3617 12.7947 19.9152 12.7947C19.4687 12.7947 19.0221 12.7947 18.6649 12.8842V0.0894775H21.1655Z" strokeWidth="0"/>
              <path d="M229.159 18.879V15.0316H227.73V14.4948H231.302V15.0316H229.784V18.879H229.159Z" strokeWidth="0"/>
              <path d="M231.927 18.879V14.4948H232.642L234.16 18.1632L235.589 14.4948H236.393V18.879H235.767V15.5684L234.428 18.879H233.892L232.552 15.5684V18.879H231.927Z" strokeWidth="0"/>
            </svg>
            <div>
              <h2>
                <LocalizedText textKey="ppt-landingpage-section-join-heading" />
              </h2>
              <p>
                <LocalizedText textKey="ppt-landingpage-section-join-p" />
              </p>
            </div>
            <Button
              className="join-button"
              size="large"
              intent='none'
              onClick={() => setActiveView(AuthenticationViews.join)}
            >
              <LocalizedText textKey="join-now" />
            </Button>
          </div>
        </section>
      </main>
      <LoginFooter />
      <CookiesBanner />
    </ParallaxProvider>
  );
}
